/* eslint-disable sort-keys */
/* eslint-disable max-len */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate
} from "react-router-dom";
import { shallow } from "zustand/shallow";
import {
  GridMain,
  HeroBanner,
  Modal,
  ModalEcmComponent,
  ModalEditionChange,
  PollingData,
  ScormTojModal,
  Spinner,
  Tabs
} from "../../components";
import {
  STATUS_LO,
  LEVEL,
  TAB_ITEMS_CONFIG
} from "../../consts";
import { UserNavigationContext } from "../../context/UserNavigationContext";
import {
  useEditionSessionsQuery,
  useEditionsQuery,
  useEnrollableEditionsQuery,
  useEnrollmentStatusQuery,
  useInvalidateEditionsQuery,
  useInvalidateLearningObjectQuery,
  usePatchMutation,
  useLearningObjectQuery,
  useRelatedLearningObjectQuery,
  useToolsQuery
} from "../../queries";
import { useSharedPosts } from "../../queries/community";
import { useGetExercisesList } from "../../queries/exercises";
import { useAuthStore, useCommunityClusterStore } from "../../stores";
import type {
  Course,
  JavaBasicDate,
  LearningActivity,
  Root,
  StatusLO
} from "../../types";
import { hasPassedExpiration } from "../../utils/datetime";
import { getError } from "../../utils/error";
import {
  filterTabTypes,
  getExploreTabProps
} from "../../utils/explore";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";

// Polling storage utilities
const EDITION_CHANGE_POLLING_KEY = "edition_change_polling";

const saveEditionChangePollingToStorage = (editionId: number, userId: number) => {
  sessionStorage.setItem(EDITION_CHANGE_POLLING_KEY, JSON.stringify({
    editionId,
    startTime: Date.now(),
    userId
  }));
};

const getEditionChangePollingFromStorage = (): PollingData | null => {
  const data = sessionStorage.getItem(EDITION_CHANGE_POLLING_KEY);
  return data ? JSON.parse(data) : null;
};

const clearEditionChangePollingFromStorage = () => {
  sessionStorage.removeItem(EDITION_CHANGE_POLLING_KEY);
};

export function ExploreDetail() {
  const isNotFoundCode = "core-005";
  const { t } = useTranslation();
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);

  const { pathname } = useLocation();

  const {
    idString,
    learningObjectTypology,
    idNumber,
    courseIdNumber,
    rootIdNumber
  } = useRouteParamsLo();

  const [statusFilter, setStatusFilter] = useState<StatusLO | null | "">(null);
  const [openModalEcm,setOpenModalEcm]=useState(false);
  const [openModalToj,setOpenModalToj]=useState(false);
  const [
    placeholderLO,
    setPlaceholderLO
  ] = useState<Course | LearningActivity | Root | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [modalErrorText, setModalErrorText] = useState("");
  const [openModalEditionChange, setOpenModalEditionChange] = useState(false);
  const [isPollingEditionChange, setIsPollingEditionChange] = useState(false);
  const [newEditionIdForPolling, setNewEditionIdForPolling] = useState<number | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingCompletedRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const enableCommunityFlag = useAuthStore(state => state.corporateInfo?.enable_community_flag);
  const corporateId = useAuthStore(state => state.userData?.organization_id);
  const initiativeId = useAuthStore(state => state.userData?.initiative_id);
  const userId = useAuthStore(state => state.userData?.id_user) ?? 0;
  const { navigation, setNavigation } = useContext(UserNavigationContext);
  const [isCurrentSession, setIsCurrentSession] = useState<boolean>(false);
  const isLearningActivity = (
    learningObjectTypology !== LEVEL.COURSE && learningObjectTypology !== LEVEL.PATH
  );
  const [isLoading, setIsLoading] = useState(false);
  const [currentLink, setCurrentLink] = useState<string>();

  useEffect(() => {
    document.body.removeAttribute("style");
    const isExpired = hasPassedExpiration(learningObject?.expirationDate);

    if (navigation?.includes(location.pathname) || (isLearningActivity && isExpired)) return;
    const currentNavigation = navigation;
    currentNavigation?.push(location.pathname);

    setNavigation && setNavigation(currentNavigation || []);
  }, [location.pathname]);

  const isCourse = (
    learningObjectTypology === LEVEL.COURSE
  );

  console.log("setIsLoading", setIsLoading);
  const {
    data: learningObject,
    error: learningObjectError,
    isFetching:learningObjectIsFetching,
    isError: learningObjectIsError,
    isSuccess: learningObjectIsSuccess,
    refetch:refetchLearningObject
  } = useLearningObjectQuery({
    courseId: courseIdNumber,
    enabled: Boolean(!isNaN(idNumber) && learningObjectTypology && corporateId && initiativeId),
    id: idNumber ?? "",
    learningObjectTypology: learningObjectTypology,
    placeholderData: placeholderLO,
    rootId: (isLearningActivity || isCourse ) ? rootIdNumber : null,
    status: statusFilter === "" ? null : statusFilter
  });

  const refetchLo = useCallback(() => {
    if (corporateId && initiativeId) {
      refetchLearningObject();
    }
  }, [corporateId, initiativeId, refetchLearningObject]);



  const {
    data: parentCourse,
    refetch:refetchParentLoCourse
  } = useLearningObjectQuery({
    enabled: Boolean(courseIdNumber && corporateId && initiativeId),
    id: courseIdNumber,
    learningObjectTypology: LEVEL.COURSE,
    rootId: (isLearningActivity || isCourse ) ? rootIdNumber : null
  });


  const refetchParentCourse = () => {
    if (corporateId && initiativeId) {
      refetchParentLoCourse();
    }
  };

  const { data: editions } = useEditionsQuery({
    classroomId: idString ?? ""
  });

  // Enrollment status query for edition change polling
  const { data: editionChangeStatusData, refetch: refetchEditionChangeStatus } = useEnrollmentStatusQuery({
    corporateId: corporateId ?? 0,
    editionId: newEditionIdForPolling ?? 0,
    enabled: false, // Disabled, use manual refetch only
    userId
  });

  const { data: related } = useRelatedLearningObjectQuery({
    learningObjectId: idString ?? ""
  });
  const { data: tools } = useToolsQuery({
    learningObjectId: learningObject?.id?.toString() ?? ""
  });

  const { data: sessions } = useEditionSessionsQuery({
    editionId: editions?.[0]?.id?.toString() ?? "",
    type: learningObject?.learningObjectTypology || ""
  });

  useEffect(() => {
    const now = Date.now();
    const orderedSessions = sessions?.sort((a, b) => a.id - b.id);

    const currentSession = orderedSessions?.find(session => {
      const startDate = new Date(Number(session.start_date) - 3600000).getTime();
      //const endDate = new Date(Number(session.end_date) + 3600000).getTime();
      const endDate = new Date(Number(session.end_date)).getTime();

      return now >= startDate && now <= endDate;
    });

    if (currentSession) {
      setIsCurrentSession(true);
      setCurrentLink(currentSession.url);
    }
  }, [sessions]);
  const availableClusters = useCommunityClusterStore((store) => store.availableClusters, shallow);
  const communityFlag = enableCommunityFlag === true && smartConfiguration?.funcCommunity === true && !!(availableClusters.find(({ is_share_enabled }) => is_share_enabled));


  const { data: sharedPosts } = useSharedPosts({ enabled: communityFlag, loId: idNumber });

  const getActivities = () => {
    if (learningObjectTypology !== LEVEL.COURSE && learningObjectTypology !== LEVEL.PATH) {
      if ((parentCourse && "learningActivities" in parentCourse)) {
        return parentCourse.learningActivities;
      }
    } else {
      if ((learningObject && "learningActivities" in learningObject)) {
        return learningObject.learningActivities;
      }
    }
  };


  const activities = getActivities() || [];
  const editionBooked = editions?.find(edition => edition.status === "E") || undefined;
  // const hasEdition =  learningObject?.status === "R" && editions?.some(edition =>
  //   edition.status !== "R"
  // ) || undefined;
  const {
    data: enrollableEditionsWithSessions,
    isFetching: enrollableEditionsWithSessionsLoading,
    refetch: refetchEnrollableEditionsWithSessions
  } = useEnrollableEditionsQuery({
    classroomId: idString ?? "",
    enabled: editionBooked?.status === "E" && editionBooked?.isAbsent === false
  });

  const { data: exercises } = useGetExercisesList({ editionId: editionBooked?.id });

  /*const exercises = !exercisesApi ? [
    {
      teacher_id: {
        id: 1,
        label: "Mario Insegnante"
      },
      exercise_id: {
        id: 1,
        label: "ES_1"
      },
      exercise_size: 1.1,
      exercise_last_update_date: "2024-01-01T14:38:39.272Z",
      revisions: [
        {
          revision_id: 1,
          assignment_id: {
            id: 1,
            label: "Svolgimento Es_1"
          },
          assignment_size: 1.2,
          assignment_last_update_date: "2024-01-02T14:38:39.272Z",
          correction_id: {
            id: 1,
            label: "Revisione_1"
          },
          correction_size: 1.3,
          correction_last_update_date: "2024-01-03T14:38:39.272Z",
          outcome: ""
        }
      ]
    },
    {
      teacher_id: {
        id: 2,
        label: "Giovanna Insegnante"
      },
      exercise_id: {
        id: 2,
        label: "ES_2"
      },
      exercise_size: 2.1,
      exercise_last_update_date: null,
      revisions: [
      ]
    }
  ]  : exercisesApi;*/

  const filteredTabTypes =
    learningObject?.learningObjectTypology ? (
      filterTabTypes({
        activities,
        editions,
        exercises,
        learningObject,
        related,
        sharedPosts,
        tabItems: TAB_ITEMS_CONFIG?.[learningObject?.learningObjectTypology].tabItems,
        tools,
        stakeholders: learningObject.stakeholders,
        hasCommunityEnabled: communityFlag
      })
    ) : [];

  const contentTabs = filteredTabTypes?.flatMap((tabType) => (
    learningObject ? (
      getExploreTabProps({
        activities,
        courseId: courseIdNumber,
        editions,
        exercises,
        filter: statusFilter,
        isDetailPage: true,
        learningObject,
        related,
        rootId: rootIdNumber,
        setFilter: setStatusFilter,
        setOpenModalEcm:setOpenModalEcm,
        sharedPosts,
        t,
        tabType,
        tools,
        enrollableEditionsWithSessions: enrollableEditionsWithSessions || [],
        isPollingEditionChange,
        onChangeEditionClick: () => setOpenModalEditionChange(true)
      })) : []
  ));

  const hasWrongType = useMemo(() => {
    if(learningObjectIsFetching || !learningObject?.learningObjectTypology || !learningObjectTypology) return false;
    return learningObject.learningObjectTypology !== learningObjectTypology;
  }, [learningObjectIsFetching, learningObject, learningObjectTypology]);


  const isNotFound = getError(learningObjectError)?.[0]?.cause === isNotFoundCode;
  const isNotEnrolled = getError(learningObjectError)?.[0]?.cause === "STUDENT_NOT_ENROLLED";

  if(isNotFound || hasWrongType){
    navigate("/not-found");
  }
  if (isNotEnrolled){
    navigate("/not-found", { state: { "message": t("student.not.enrolled") } });
  }

  useEffect(() => {
    if(learningObjectError && !isNotFound && !showModal) setShowModal(true);
  }, [learningObjectError, isNotFound, showModal]);

  useEffect(() => {
    if (learningObjectIsSuccess) {
      setPlaceholderLO(learningObject);
    }
  }, [learningObject, learningObjectIsSuccess]);
  const isToj = learningObject?.learningObjectTypology === LEVEL.DA_FINAL_BALANCE ||
  learningObject?.learningObjectTypology === LEVEL.DA_GOALS ||
  learningObject?.learningObjectTypology === LEVEL.DA_MATERIAL ||
  learningObject?.learningObjectTypology === LEVEL.DA_MEETING;

  window.addEventListener("message", (e) => {
    if (e.data == "closeModal" && isToj) {
      setOpenModalToj(false);
      refetchLo();

      if(courseIdNumber){
        refetchParentCourse();
      }

    }
  });

  const endDate = learningObject?.ecmEndDate ?
    [ learningObject?.ecmEndDate?.[0],
      learningObject?.ecmEndDate?.[1],
      learningObject?.ecmEndDate?.[2]
    ]  as JavaBasicDate :

    learningObject?.editionsTojDetails?.[0] ?
        [
          learningObject.editionsTojDetails[0].endTime?.[0],
          learningObject.editionsTojDetails[0].endTime?.[1],
          learningObject.editionsTojDetails[0].endTime?.[2]
        ]  as JavaBasicDate
      : undefined ;

  const startDate = learningObject?.ecmStartDate ?  [
    learningObject?.ecmStartDate?.[0],
    learningObject?.ecmStartDate?.[1],
    learningObject?.ecmStartDate?.[2]
  ] as JavaBasicDate :
    learningObject?.editionsTojDetails?.[0] ?
        [
          learningObject.editionsTojDetails[0].startTime?.[0],
          learningObject.editionsTojDetails[0].startTime?.[1],
          learningObject.editionsTojDetails[0].startTime?.[2]
        ]   as JavaBasicDate
      : undefined;

  useEffect(() => {
    if (learningObjectIsFetching) return;
    refetchLo();
  }, [pathname]);

  useEffect(() => {
    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      refetchLo();
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, []);


  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {

    const handleVisibilityChange = () => {
      if (!document.hidden &&
      (  learningObjectTypology !== LEVEL.DA_PHYSICAL_CLASS &&
        learningObjectTypology !== LEVEL.DA_VIRTUAL_CLASS &&
        learningObjectTypology !== LEVEL.DA_MEETING
      )
      ){
        refetchLo();
        if(courseIdNumber){
          refetchParentCourse();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { invalidateLearningObject } = useInvalidateLearningObjectQuery({
    courseId: courseIdNumber,
    id: idNumber ?? 0,
    learningObjectTypology,
    rootId: rootIdNumber
  });

  const { invalidateEditions } = useInvalidateEditionsQuery({
    classroomId: idString ?? "",
    scrollToTop: false
  });

  // Restore polling from sessionStorage on mount
  useEffect(() => {
    const savedPolling = getEditionChangePollingFromStorage();
    if (savedPolling && savedPolling.userId === userId) {
      // Resume polling with the saved edition ID
      setNewEditionIdForPolling(savedPolling.editionId);
      pollingCompletedRef.current = false;
      setIsPollingEditionChange(true);
    }
  }, [userId]);

  // Polling logic with progressive intervals
  useEffect(() => {
    if (!isPollingEditionChange) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }

    const startTime = Date.now();
    const MAX_POLLING_DURATION = 120000; // 2 minutes
    const MAX_INTERVAL = 20000; // 20 seconds
    let currentInterval = 5000; // Start with 5 seconds

    const scheduleNextPoll = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= MAX_POLLING_DURATION) {
        // Timeout reached
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        clearEditionChangePollingFromStorage();
        setModalErrorText(t("enrollment-polling-error"));
        setShowModal(true);
        invalidateLearningObject();
        invalidateEditions();
        refetchEnrollableEditionsWithSessions();
        return;
      }

      // Refetch enrollment status
      refetchEditionChangeStatus();

      // Increment interval by 5s if below max
      if (currentInterval < MAX_INTERVAL) {
        currentInterval += 5000;
      }

      // Schedule next poll
      pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);
    };

    // First call after initial interval (5s)
    pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);

    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [isPollingEditionChange, refetchEditionChangeStatus, invalidateLearningObject, invalidateEditions, refetchEnrollableEditionsWithSessions, t]);

  // Handle edition change status response
  useEffect(() => {
    if (!editionChangeStatusData?.status) return;

    // If polling is already completed, do nothing
    if (pollingCompletedRef.current) return;

    // Only process status changes if polling is active
    if (!isPollingEditionChange) return;

    const currentStatus = editionChangeStatusData.status;
    const currentErrorMessage = editionChangeStatusData.errorMessage;

    if (currentStatus === "IN_PROGRESS") {
      // Already polling, continue
      // Status is still in progress, keep polling
    } else if (currentStatus === "COMPLETE" || currentStatus === "ERROR") {
      // Mark polling as completed FIRST to avoid loops
      pollingCompletedRef.current = true;

      // Stop polling
      setIsPollingEditionChange(false);

      // Remove from sessionStorage
      clearEditionChangePollingFromStorage();

      // Show modal with result
      if (currentStatus === "COMPLETE") {
        setModalErrorText(t("enrollment-submitted-success"));
        setShowModal(true);
      } else if (currentStatus === "ERROR" && currentErrorMessage.includes("[GET-ENROLLMENT-STATUS-ERROR]")){
        setModalErrorText(t("max-capacity-reached-error"));
        setShowModal(true);
      } else {
        setModalErrorText(t("enrollment-polling-error"));
        setShowModal(true);
      }

      // Invalidate and refetch
      invalidateLearningObject();
      invalidateEditions();
      refetchEnrollableEditionsWithSessions();
      refetchLo();
    }
    // NOT_ENROLLED: do nothing, normal behavior
  }, [editionChangeStatusData?.status, isPollingEditionChange, invalidateLearningObject, invalidateEditions, refetchEnrollableEditionsWithSessions, refetchLo, t]);

  const {
    mutate: changeEditionMutate,
    isLoading: isChangingEdition
  } = usePatchMutation({
    learningObjectId: idNumber ?? 0,
    learningObjectTypology: learningObjectTypology ?? "",
    isToj
  });

  const handleEditionChange = useCallback((newEditionId: number) => {
    changeEditionMutate(newEditionId, {
      onError: (error) => {
        const parsedError = getError(error);
        const maximumError = parsedError?.[0]?.message.includes("participants maximum number");
        const alreadyAssociatedError = parsedError?.[0]?.message.includes("already associated with user");
        const maxCapacityReached = parsedError?.[0]?.message.includes("MAX_EDITION_CAPACITY_REACHED_DURING_UPDATE_ENROLLMENTS");

        if (maxCapacityReached) {
          setOpenModalEditionChange(false);
          setShowModal(true);
          setModalErrorText(t("max-capacity-reached-error"));
          invalidateEditions();
          refetchEnrollableEditionsWithSessions();
          return;
        }

        if (parsedError?.[0]?.name === "FATAL") {
          const errorKey = JSON.parse(error as string)?.errors["FATAL"]?.[0]?.key;
          if (errorKey === "bad.request.user.already.associated") {
            setModalErrorText(t("already-associated-error"));
          } else {
            setModalErrorText(parsedError?.[0]?.message || t("generic_error"));
          }
        } else if (maximumError) {
          setModalErrorText(t("maximum-participants-error"));
        } else if (alreadyAssociatedError) {
          setModalErrorText(t("already-associated-error"));
        } else {
          setModalErrorText(t("enrollment-generic-error"));
        }

        invalidateEditions();

        setOpenModalEditionChange(false);
        setShowModal(true);
      },
      onSuccess: () => {
        setOpenModalEditionChange(false);

        // Show success modal
        setModalErrorText(t("enrollment-edition-success"));
        setShowModal(true);

        // Set the new edition ID for polling
        setNewEditionIdForPolling(newEditionId);

        // Reset polling completed flag
        pollingCompletedRef.current = false;

        // Save to storage and start polling
        saveEditionChangePollingToStorage(newEditionId, userId);
        setIsPollingEditionChange(true);

        // Invalidate to prepare for updates
        invalidateEditions();
        refetchEnrollableEditionsWithSessions();
      }
    });
  }, [changeEditionMutate, invalidateEditions, refetchEnrollableEditionsWithSessions, t, userId]);

  const isVirtualClass = learningObject?.learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS;

  return (
    <>
      {
        ((!learningObject && learningObjectIsFetching) || isLoading || enrollableEditionsWithSessionsLoading) ? (
          <div style={ {
            flexGrow:1
          } } >
            <Spinner/>
          </div>
        ) :
          (
            isNotFound || !learningObject ? (
              <p>error occurred</p>
            ) : (
              <>
                <HeroBanner
                  attributes={ learningObject.attributes }
                  badge={ learningObject.badge }
                  booked={
                    (learningObject.status === STATUS_LO.E && learningObject.enrollmentEditionId != null)
                    || (learningObject.status === STATUS_LO.P)
                  }
                  completable={ learningObject.completable }
                  categories={ learningObject.topicTags || [] }
                  certificate={ learningObject.certificate }
                  certificates={ learningObject.certificates || [] }
                  coverVideoPublicUrl={ learningObject?.coverVideoPublicURL }
                  courseId={ courseIdNumber }
                  coverPublicURL={ learningObject.coverPublicURL?.toString() || "" }
                  duration={ learningObject.duration }
                  ecmType={ learningObject.ecmType }
                  editionNumber={ editions?.length }
                  //editionVirtualLink={ editionBooked?.link }
                  editionVirtualLink={ currentLink || null }
                  expirationDate={ learningObject?.expirationDate }
                  endDate={ endDate }
                  enrollType={ learningObject.enrollType }
                  id={ learningObject?.id }
                  ecmRegistration={ learningObject.ecmRegistration }
                  ecm_specialization={ learningObject.ecm_specialization }
                  editionsLength = { editions?.length || 0 }
                  isEnable = { learningObject.isEnable }
                  isFavourite={ learningObject?.isFavourite }
                  isMandatory={ learningObject.isMandatory }
                  isSurvey={ "isSurvey" in learningObject ? learningObject.isSurvey : false }
                  isTest={  "isTest" in learningObject ? learningObject.isTest : false }
                  isToj={ learningObject?.isToj }
                  learningObjectType={ learningObject.learningObjectType }
                  learningObjectTypology={
                    learningObject.learningObjectTypology
                    || learningObjectTypology
                  }
                  meetingDetails={ learningObject?.meetingDetails }
                  percentageOfCompletion={ learningObject.percentageOfCompletion }
                  rating={ learningObject.averageRatingScore ?? 0 }
                  rootId={ rootIdNumber }
                  setOpenModalEcm={ setOpenModalEcm }
                  setOpenTojModal={ setOpenModalToj }
                  shortDescription={ learningObject.shortDescription }
                  startDate={ startDate }
                  status={ learningObject?.status }
                  title={ learningObject.title }
                  isCurrentSession={ isCurrentSession }
                  canAccessNow={ learningObject.canAccessNow }
                  isLinkRelative={ learningObject.isLinkRelative }
                  link={ learningObject.link }
                  credits={ learningObject.credits }
                  attributesIds={ learningObject.attributes }
                />
                <GridMain  component="div" padding={ 0 }>
                  <Tabs
                    contents={ contentTabs }
                    titleInset={ true }
                  />
                </GridMain>
              </>
            )
          )
      }

      {
        learningObject && learningObject.enrollId && openModalEcm ?
          <ModalEcmComponent
            courseId={ courseIdNumber }
            open={ openModalEcm }
            invalidateLearningObects={ ()=> {
              refetchLo();
              if(courseIdNumber){
                refetchParentCourse();
              }

            } }
            loId={ idNumber }
            enrollId={ learningObject?.enrollId }
            onClose={ ()=> setOpenModalEcm(false) }
            rootId={ rootIdNumber }
            sponsors={ learningObject?.sponsors }
          /> : null
      }
      {
        openModalToj && learningObject ?

          <ScormTojModal
            activityLevel={ learningObject?.learningObjectTypology  }
            idString={ learningObject.id }
            open={ openModalToj }
            courseId={ courseIdNumber?.toString() ?? null }
            pathId={ rootIdNumber?.toString() ?? null }
          /> :
          null
      }
      {
        openModalEditionChange && !isPollingEditionChange && enrollableEditionsWithSessions && editionBooked?.status === "E" ?
          <ModalEditionChange
            open={ openModalEditionChange }
            handleClose={ () => setOpenModalEditionChange(false) }
            enrollableEditionsWithSessions={ enrollableEditionsWithSessions }
            onEditionChange={ handleEditionChange }
            isVirtual={ isVirtualClass }
            isLoading={ isChangingEdition }
            isPollingEditionChange={ isPollingEditionChange }
          />
          : null
      }
      <Modal
        description={ modalErrorText || (getError(learningObjectError)?.[0]?.message ?? "") }
        open={ showModal }
        title={
          modalErrorText
            ? t("attention")
            : getError(learningObjectError)?.[0]?.name === "FATAL"
              ? t("attention")
              : (getError(learningObjectError)?.[0]?.name ?? "")
        }
        onClose={ ()=> {
          setShowModal(false);
          setModalErrorText("");
          if (learningObjectIsError) {
            navigate(-1);
          }
        } }
      />
    </>
  );
}
